import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import { useApp } from '@weke/ui';
import { defaultLayout, serializeLayout } from '@weke/ui';
export type ChromePanel = 'watch' | 'navigator' | 'toolbox';

/**
 * The top chrome: a menu strip and a toolbar.
 *
 * The menus are honest about what exists — every item that has no implementation
 * is rendered disabled with the reason in its `title`, rather than opening a
 * dialog that does nothing. `File` carries the two actions the terminal really
 * has (export the statement, sign out); `View` toggles the panels; `Charts`
 * changes the interval; `Help` shows the keyboard map.
 */
interface ChromeProps {
  readonly onSignOut: () => void;
  readonly onExport: () => void;
  readonly onInterval: (minutes: number) => void;
  readonly onTogglePanel: (id: ChromePanel) => void;
  readonly onResetLayout: () => void;
  readonly onNewChart: () => void;
  readonly onReload: () => void;
}

export function MenuBar(props: ChromeProps): ReactElement {
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    if (open === null) return;
    const close = (): void => setOpen(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  const menu = (name: string, items: ReactElement[]): ReactElement => (
    <div className="relative" key={name}>
      <div
        className="menu-item"
        role="menuitem"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open === name}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(open === name ? null : name);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') setOpen(open === name ? null : name);
        }}
      >
        {name}
      </div>
      {open === name ? (
        <div
          className="absolute left-0 top-full z-30 min-w-52 border border-line-strong bg-surface py-1 shadow-lg"
          role="menu"
        >
          {items}
        </div>
      ) : null}
    </div>
  );

  const item = (label: string, onClick: (() => void) | null, note?: string): ReactElement => (
    <button
      key={label}
      type="button"
      role="menuitem"
      className="flex w-full items-center justify-between gap-6 px-3 py-1 text-left text-text hover:bg-[#2b2b2b] disabled:text-faint"
      disabled={onClick === null}
      title={onClick === null ? note : undefined}
      onClick={() => {
        setOpen(null);
        onClick?.();
      }}
    >
      <span>{label}</span>
      {note === undefined ? null : <span className="text-2xs faint">{note}</span>}
    </button>
  );

  return (
    <div className="menubar" role="menubar" aria-label="Main menu">
      <span className="mr-3 pl-2 text-2xs tracking-widest text-text">WEKE</span>
      {menu('File', [
        item('Export statement (CSV)', props.onExport),
        item('Print', null, 'not implemented'),
        item('Sign out', props.onSignOut),
      ])}
      {menu('View', [
        item('Market watch', () => props.onTogglePanel('watch')),
        item('Navigator', () => props.onTogglePanel('navigator')),
        item('Toolbox', () => props.onTogglePanel('toolbox')),
        item('Reset layout', props.onResetLayout),
      ])}
      {menu('Charts', [
        item('M1', () => props.onInterval(1)),
        item('M5', () => props.onInterval(5)),
        item('M15', () => props.onInterval(15)),
        item('H1', () => props.onInterval(60)),
        item('H4', () => props.onInterval(240)),
        item('D1', () => props.onInterval(1440)),
        item('Indicators', null, 'not implemented'),
      ])}
      {menu('Tools', [
        item('Options', null, 'not implemented'),
        item('Reset layout', props.onResetLayout),
      ])}
      {menu('Window', [
        item('New chart tab', props.onNewChart),
        item('Close all charts', null, 'use the tab close buttons'),
      ])}
      {menu('Help', [
        item('Keyboard: Alt+B / Alt+S, Enter sends, Esc dismisses', null, 'see docs/12'),
        item('About Weke Trader', null, 'P5 build'),
      ])}
    </div>
  );
}

export function Toolbar(props: {
  readonly onSignOut: () => void;
  readonly onExport: () => void;
  readonly onInterval: (minutes: number) => void;
  readonly onTogglePanel: (id: ChromePanel) => void;
  readonly onReload: () => void;
}): ReactElement {
  const app = useApp();
  const account = app.account;
  const styles: Record<
    string,
    { readonly label: string; readonly tone: string; readonly glyph: string }
  > = {
    live: { label: 'Live', tone: 'border-[#1f6b3f] text-buy', glyph: '●' },
    replaying: { label: 'Replaying', tone: 'border-line-strong text-warning', glyph: '◌' },
    connecting: { label: 'Connecting', tone: 'border-line-strong text-warning', glyph: '◌' },
    offline: { label: 'Offline', tone: 'border-[#7a2a26] text-sell', glyph: '○' },
    fatal: { label: 'Signed out', tone: 'border-[#7a2a26] text-sell', glyph: '○' },
  };
  const style = styles[app.status] ?? styles.connecting;
  const connectionLabel = style?.label ?? 'Connecting';
  const connectionTone = style?.tone ?? '';
  const connectionGlyph = style?.glyph ?? '◌';  const glyphs: { label: string; title: string; onClick: () => void }[] = [
    { label: '▤', title: 'Toggle the market watch', onClick: () => props.onTogglePanel('watch') },
    { label: '☰', title: 'Toggle the navigator', onClick: () => props.onTogglePanel('navigator') },
    { label: '▦', title: 'Toggle the toolbox', onClick: () => props.onTogglePanel('toolbox') },
    { label: '⟳', title: 'Reload history and the account snapshot', onClick: props.onReload },
    { label: '⤓', title: 'Export the statement as CSV', onClick: props.onExport },
    { label: '⏻', title: 'Sign out', onClick: props.onSignOut },
  ];

  return (
    <div className="toolbar" role="toolbar" aria-label="Toolbar">
      <span
        className={`ml-1 inline-flex h-[17px] items-center gap-1 border px-1 ${connectionTone}`}
        data-testid="connection-state"
        title={app.statusDetail ?? 'connection state'}
      >
        {connectionGlyph}
        <span>{connectionLabel}</span>
      </span>
      <Sep />
      <span className="px-2 text-text">
        {account === null ? '…' : `${account.login} · ${account.currency}`}
      </span>
      <select
        className="field w-28"
        aria-label="Account"
        value={account?.account_id ?? ''}
        disabled={true}
        title="One login maps to one account in this build"
        data-testid="account-selector"
        onChange={() => undefined}
      >
        {account === null ? <option value="">—</option> : null}
        <option value={account?.account_id ?? ''}>
          {account === null ? '—' : `${account.login} · ${account.currency}`}
        </option>
      </select>
      <Sep />
      {glyphs.map((glyph) => (
        <button
          key={glyph.label}
          type="button"
          className="glyph"
          title={glyph.title}
          aria-label={glyph.title}
          onClick={glyph.onClick}
        >
          {glyph.label}
        </button>
      ))}
      <Sep />
      <span className="px-2 text-2xs text-muted">Interval</span>
      {[1, 5, 15, 60, 240, 1440].map((minutes) => (
        <button
          key={minutes}
          type="button"
          className="btn btn-flat h-[19px] px-2 text-2xs"
          onClick={() => props.onInterval(minutes)}
        >
          {minutes === 1440 ? 'D1' : minutes >= 60 ? `H${minutes / 60}` : `M${minutes}`}
        </button>
      ))}
      <span className="ml-auto pr-2 text-2xs faint">
        {app.symbols.length} symbols · {app.heartbeatMs} ms heartbeat
      </span>
    </div>
  );
}

function Sep(): ReactElement {
  return <span className="mx-1 h-4 w-px bg-line-strong" aria-hidden="true" />;
}

/** The layout snapshot the navigator offers to restore. */
export function useLayoutPreset(): { readonly name: string; readonly json: string } {
  return useMemo(() => ({ name: 'Default', json: serializeLayout(defaultLayout()) }), []);
}
